import { computed, onBeforeUnmount, onMounted, ref, watch, type Ref } from "vue";
import type { DSFileUploadComponentProps, DSFileUploadEmits } from "../types";
import type { DSFocusable } from "@/src/design_system/shared/types/expose";
import { useDSInput } from "@/src/design_system/form_elements/shared/composables/useDSInput";

export interface DSFileUploadDropzoneExpose extends DSFocusable {
  openFileDialog: () => void;
  resetInput: () => void;
  setInputFiles: (files: File[]) => void;
}

const OPEN_MODAL_SELECTOR = 'dialog[open][aria-modal="true"], [aria-modal="true"]:not(dialog)';

interface UseDSFileUploadInteractionsOptions {
  rootRef: Ref<HTMLElement | undefined>;
  props: DSFileUploadComponentProps;
  emit: DSFileUploadEmits;
  onFilesSelected: (files: File[], event: Event | DragEvent) => void;
}

export const useDSFileUploadInteractions = ({
  props,
  emit,
  onFilesSelected,
  rootRef,
}: UseDSFileUploadInteractionsOptions) => {
  const dropzoneRef = ref<DSFileUploadDropzoneExpose>();
  const dragDepth = ref(0);
  const isDropzoneFocused = ref(false);
  const isPageDragActive = ref(false);
  const isDragActive = computed(() => dragDepth.value > 0);
  let removePageDropListeners: (() => void) | undefined;
  let stopWatchingPageDropzone: (() => void) | undefined;

  const { focus } = useDSInput(dropzoneRef);

  const blur = (): void => {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && rootRef.value?.contains(activeElement)) activeElement.blur();
  };

  const openFileDialog = (): void => {
    dropzoneRef.value?.openFileDialog();
  };

  const resetInput = (): void => {
    dropzoneRef.value?.resetInput();
  };

  const setInputFiles = (files: File[]): void => {
    dropzoneRef.value?.setInputFiles(files);
  };

  const onFocus = (event: FocusEvent): void => {
    if (event.relatedTarget instanceof Node && rootRef.value?.contains(event.relatedTarget)) return;
    emit("focus", event);
  };

  const onBlur = (event: FocusEvent): void => {
    if (event.relatedTarget instanceof Node && rootRef.value?.contains(event.relatedTarget)) return;
    emit("blur", event);
  };

  const onDragEnter = (): void => {
    if (props.disabled) return;

    dragDepth.value += 1;
  };

  const onDragOver = (event: DragEvent): void => {
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = props.disabled ? "none" : "copy";
    }
  };

  const onDragLeave = (): void => {
    if (props.disabled) return;

    dragDepth.value = Math.max(0, dragDepth.value - 1);
  };

  const getDroppedFiles = (event: DragEvent): File[] => Array.from(event.dataTransfer?.files ?? []);

  const onDrop = (event: DragEvent): void => {
    dragDepth.value = 0;
    onFilesSelected(getDroppedFiles(event), event);
  };

  const isFileDrag = (event: DragEvent): boolean => {
    return Array.from(event.dataTransfer?.types ?? []).includes("Files");
  };

  const isCKEditorDropTarget = (event: DragEvent): boolean => {
    return event.composedPath().some(target => target instanceof Element && target.matches(".ck-editor__editable"));
  };

  const isOwnDropTarget = (event: DragEvent): boolean => {
    return event.target instanceof Node && Boolean(rootRef.value?.contains(event.target));
  };

  const isForeignDropTarget = (event: DragEvent): boolean => {
    if (isCKEditorDropTarget(event)) return true;

    return event.defaultPrevented && !isOwnDropTarget(event);
  };

  const isCoveredByModal = (): boolean => {
    const root = rootRef.value ?? null;
    return Array.from(document.querySelectorAll(OPEN_MODAL_SELECTOR)).some(modal => !modal.contains(root));
  };

  const isPageFileDrag = (event: DragEvent): boolean => {
    return isFileDrag(event) && !isCoveredByModal();
  };

  const onPageDragEnter = (event: DragEvent): void => {
    if (props.disabled || !isPageFileDrag(event)) return;
    if (isForeignDropTarget(event)) {
      isPageDragActive.value = false;
      return;
    }

    event.preventDefault();
    isPageDragActive.value = true;
  };

  const onPageDragOver = (event: DragEvent): void => {
    if (props.disabled || !isPageFileDrag(event)) return;
    if (isForeignDropTarget(event)) {
      isPageDragActive.value = false;
      return;
    }

    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
  };

  const onPageDragLeave = (event: DragEvent): void => {
    if (event.relatedTarget === null) isPageDragActive.value = false;
  };

  const onPageDrop = (event: DragEvent): void => {
    if (!isPageFileDrag(event)) return;

    isPageDragActive.value = false;
    if (isForeignDropTarget(event)) return;

    event.preventDefault();
    if (props.disabled || isOwnDropTarget(event)) return;

    onFilesSelected(getDroppedFiles(event), event);
  };

  const addPageDropListeners = (): void => {
    window.addEventListener("dragenter", onPageDragEnter);
    window.addEventListener("dragover", onPageDragOver);
    window.addEventListener("dragleave", onPageDragLeave);
    window.addEventListener("drop", onPageDrop);

    removePageDropListeners = () => {
      window.removeEventListener("dragenter", onPageDragEnter);
      window.removeEventListener("dragover", onPageDragOver);
      window.removeEventListener("dragleave", onPageDragLeave);
      window.removeEventListener("drop", onPageDrop);
      removePageDropListeners = undefined;
      isPageDragActive.value = false;
    };
  };

  onMounted(() => {
    stopWatchingPageDropzone = watch(
      () => [props.fullPageDropzone, props.disabled] as const,
      ([enabled, disabled]) => {
        removePageDropListeners?.();
        if (enabled && !disabled) addPageDropListeners();
      },
      { immediate: true }
    );
  });

  onBeforeUnmount(() => {
    stopWatchingPageDropzone?.();
    removePageDropListeners?.();
  });

  return {
    blur,
    dropzoneRef,
    focus,
    isDragActive,
    isDropzoneFocused,
    isPageDragActive,
    onBlur,
    onDragEnter,
    onDragLeave,
    onDragOver,
    onDrop,
    onFocus,
    openFileDialog,
    resetInput,
    setInputFiles,
  };
};
